import { useCallback, useEffect, useState } from 'react';
import { useTestContext } from '../../../context/testContext';
import { useAppointment } from '../../../context/appointmentContext';
import { client } from '../../../helpers/api-client';
import { useResultData } from '../../../helpers/useResultData';
import CSTableResults from '../../../components/ContrastSensitivity/CSTableResults';
import CSGraphResults from '../../../components/ContrastSensitivity/CSGraphResults';
import { Button, PanelText, SessionActions, useSessionUI } from '../../../ui';
import { StepBody, useConfirmLeave } from '../testScreenKit';

// Step 4 - results (Figma session/14 middle = table, right = graph). Save goes through confirm().
const ContrastSensitivityResults = ({ localResults, setActiveTab, updateStep, defaultShowGraph = false }) => {
  const [tableResults, setTableResults] = useState([]);
  const [graphResults, setGraphResults] = useState([]);
  const noResults = !tableResults.length && !graphResults.length;
  const [showGraph, setShowGraph] = useState(defaultShowGraph);
  const [offerGraphOption, setOfferGraphOption] = useState(false);
  const { appointment } = useAppointment();
  const { clearAllResults } = useResultData();
  const { confirm, toast } = useSessionUI();
  const confirmLeave = useConfirmLeave();
  const [saving, setSaving] = useState(false);
  const {
    testData: { testResultsId },
  } = useTestContext();

  const getRemoteTestResults = useCallback(() => {
    client(`api/appointment-test-result/${testResultsId}`).then(
      ({ result_data }) => {
        if (result_data) setTableResults(JSON.parse(result_data));
      },
      (error) => {
        console.error(error);
      },
    );
  }, [testResultsId]);

  useEffect(() => {
    if (!localResults) return getRemoteTestResults();
  }, [localResults, getRemoteTestResults]);

  useEffect(() => {
    if (!localResults) return;
    const rawResults = localResults.contrastSensitivityResults;

    rawResults
      .sort((a, b) => {
        return a.logmarTested > b.logmarTested ? 1 : b.logmarTested > a.logmarTested ? -1 : 0;
      })
      .reverse();

    const parsedResults = Object.values(
      rawResults.reduce((resultsObj, { logmarTested, ...eyeResults }) => {
        if (!resultsObj[logmarTested]) resultsObj[logmarTested] = {};
        resultsObj[logmarTested] = {
          logmarTested,
          ...resultsObj[logmarTested],
          ...eyeResults,
        };
        return resultsObj;
      }, {}),
    );

    setTableResults(parsedResults);
  }, [localResults]);

  useEffect(() => {
    if (!tableResults.length) return;
    let parsedResults = [];
    let filteredResults = [];
    tableResults.forEach((result) => {
      const { logmarTested, leftEye, rightEye, bothEyes } = result;
      parsedResults.push({
        name: logmarTested,
        Left: parseFloat(leftEye?.toFixed(2)),
        Right: parseFloat(rightEye?.toFixed(2)),
        Binocular: parseFloat(bothEyes?.toFixed(2)),
      });
    });

    parsedResults.forEach((result) => {
      filteredResults.push(
        Object.entries(result).reduce((resultObj, [eyeTested, value]) => (value ? ((resultObj[eyeTested] = value), resultObj) : resultObj), {}),
      );
    });

    setGraphResults(filteredResults);
  }, [tableResults]);

  useEffect(() => {
    if (!tableResults.length) return;
    if (
      tableResults.filter((result) => {
        return result.hasOwnProperty('leftEye');
      }).length >= 2
    ) {
      setOfferGraphOption(true);
    }
    if (
      tableResults.filter((result) => {
        return result.hasOwnProperty('rightEye');
      }).length >= 2
    ) {
      setOfferGraphOption(true);
    }
    if (
      tableResults.filter((result) => {
        return result.hasOwnProperty('bothEyes');
      }).length >= 2
    ) {
      setOfferGraphOption(true);
    }
  }, [tableResults]);

  const saveTestResults = async () => {
    const { corrected } = localResults.contrastSensitivityResults;
    const sendTestResultData = {
      appointment_eye_test: localResults.eyeTestId,
      appointment: appointment.currentAppointment.id,
      result_data: JSON.stringify([...tableResults, { corrected: corrected }]),
    };

    if ((await confirm({ message: 'Are you sure you want to save the test results?' })) === true) {
      setSaving(true);
      client(`api/appointment-test-result/`, { body: sendTestResultData }).then(
        () => {
          setSaving(false);
          toast({ tone: 'success', title: 'Results saved', body: 'Contrast Sensitivity results were added to this appointment.' });
          setActiveTab('results');
        },
        (error) => {
          setSaving(false);
          console.error('Unable to save results', error);
          toast({ tone: 'error', title: 'Could not save the results', body: 'Please try again.' });
        },
      );
    }
  };

  const restartTest = () => {
    confirmLeave('restart').then(() => {
      clearAllResults();
      return updateStep('eyeSetup');
    });
  };

  return (
    <StepBody gap={24} center={noResults} data-testid='cs-results'>
      {noResults ? (
        <PanelText>No results to display.</PanelText>
      ) : (
        <>
          <div style={{ flex: 1, minHeight: showGraph ? 260 : 0, display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', overflow: 'auto' }}>
            {!showGraph ? (
              <div style={{ width: '100%', maxWidth: 560, alignSelf: 'center' }}>
                <CSTableResults results={tableResults} />
              </div>
            ) : (
              <div style={{ width: '100%', height: '100%', minHeight: 260 }} data-testid='cs-results-graph'>
                <CSGraphResults results={graphResults} />
              </div>
            )}
          </div>
          {offerGraphOption && (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <Button size='lg' onClick={() => setShowGraph(!showGraph)}>
                {!showGraph ? 'Show Graph' : 'Show Table'}
              </Button>
            </div>
          )}
        </>
      )}
      <SessionActions
        onBack={() => (localResults ? restartTest() : setActiveTab('results'))}
        backLabel={localResults ? 'Restart' : 'Back'}
        primary={localResults && !noResults ? { label: saving ? 'Saving…' : 'Save Test Results', onClick: () => saveTestResults(), disabled: saving } : undefined}
      />
    </StepBody>
  );
};

export default ContrastSensitivityResults;
